import { useEffect, useId, useMemo, useRef, useState } from 'react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@components/atoms/DropdownMenu'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { useCopied } from '@hooks/useCopied'
import { useMediaQuery } from '@hooks/useMediaQuery'
import { scopedTranslation } from '@utils/I18n'

import { calendarButtonEvent } from '@utils/embeds/calendarButton'
import { loadCalendarButton, styleCalendarList } from '@utils/embeds/loadCalendarButton'
import { formatDistance } from '@utils/embeds/distance'
import type { LinkTarget } from '@utils/embeds/embedConfig'
import { eventDates } from '@utils/embeds/eventDates'
import { allowsFeature } from '@utils/embeds/permissionsPolicy'
import { introText, venueText, type PublicEvent } from '@utils/embeds/publicEvent'
import { CoverageBar } from './CoverageBar'
import { EmbedButton } from './EmbedButton'
import { EventDateLink, EventPageLink, EventTitleLink } from './EventPageLink'

const t = scopedTranslation('embeds.events')

const AFTER_SEPARATOR = "tw:before:mx-1 tw:before:content-['·']"

const LATER_DATES_SHOWN = 3

type EventCardProps = {
  headingLevel: 2 | 3 | 4
  event: PublicEvent
  links: LinkTarget
  rsvpLabel: string
  showCoverage: boolean
  showEventType: boolean
  showIntro: boolean
  locale: string
  distance?: number
  laterDates?: PublicEvent[]
  autoFocus?: boolean
}

export function EventCard({
  headingLevel,
  event,
  links,
  rsvpLabel,
  showCoverage,
  showEventType,
  showIntro,
  locale,
  distance,
  laterDates = [],
  autoFocus,
}: EventCardProps) {
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (autoFocus) headingRef.current?.focus()
  }, [autoFocus])

  const Heading = `h${headingLevel}` as const
  const dates = eventDates(event, locale)
  const multiDay = dates !== null && dates.days > 1
  const spots = event.spots.self_sign_up
  const coverage = showCoverage && spots.total > 0
  const full = coverage && spots.filled >= spots.total
  const venue = event.online ? t('online') : venueText(event.venue)
  const intro = useMemo(() => (showIntro ? introText(event.intro) : null), [showIntro, event.intro])
  const laterDatesId = useId()
  const unlistedDateCount = laterDates.length - LATER_DATES_SHOWN

  return (
    <article className="tw:flex tw:h-full tw:flex-col tw:rounded-(--embed-radius,1rem) tw:border tw:border-gray-200 tw:bg-white">
      <div className="tw:flex tw:flex-1 tw:flex-wrap tw:gap-3 tw:p-4">
        {dates && (
          <div
            aria-hidden
            className="tw:flex tw:h-14 tw:min-w-16 tw:shrink-0 tw:flex-col tw:items-center tw:justify-center tw:whitespace-nowrap tw:rounded-(--embed-radius-sm,0.5rem) tw:bg-(--embed-brand-soft) tw:px-1.5 tw:text-(--embed-brand-ink)"
          >
            <span className="tw:text-lg tw:font-bold tw:leading-none">{dates.day}</span>
            <span className="tw:text-xs tw:font-semibold tw:uppercase">{dates.month}</span>
          </div>
        )}
        <div className="tw:flex tw:min-w-0 tw:flex-[1_1_12rem] tw:flex-col tw:gap-2 tw:wrap-break-word">
          {(event.online || showEventType) && (
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
              {event.online && (
                <span className="tw:rounded-full tw:bg-sky-50 tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium tw:text-sky-700">
                  {t('online')}
                </span>
              )}
              {showEventType && (
                <span className="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-gray-600">
                  {event.event_type.name}
                </span>
              )}
            </div>
          )}
          <EventTitleLink event={event} links={links}>
            <Heading
              ref={headingRef}
              tabIndex={-1}
              className="tw:text-base tw:font-semibold tw:leading-snug tw:text-gray-900"
            >
              {event.name}
            </Heading>
          </EventTitleLink>
          <div className="tw:flex tw:flex-col tw:gap-1 tw:text-sm tw:text-gray-700">
            {dates && (
              <span className="tw:flex tw:items-start tw:gap-1.5">
                <Icon type="calendar" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-400" />
                <span>
                  {dates.when}
                  {multiDay && (
                    <span className={cn('tw:text-gray-500', AFTER_SEPARATOR)}>{t('days', { count: dates.days })}</span>
                  )}
                </span>
              </span>
            )}
            {(venue || distance !== undefined) && (
              <span className="tw:flex tw:items-start tw:gap-1.5">
                <Icon type="location" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-gray-400" />
                <span>
                  {venue}
                  {distance !== undefined && (
                    <span className={cn('tw:font-medium tw:text-gray-900', venue && AFTER_SEPARATOR)}>
                      {t('distance_away', { distance: formatDistance(distance, locale) })}
                    </span>
                  )}
                </span>
              </span>
            )}
          </div>
          {laterDates.length > 0 && (
            <div className="tw:flex tw:flex-col tw:gap-1 tw:text-sm">
              <p id={laterDatesId} className="tw:text-xs tw:font-semibold tw:text-gray-600">
                {event.series_name ? t('later_dates_for', { name: event.series_name }) : t('later_dates')}
              </p>
              <ul aria-labelledby={laterDatesId} className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1 tw:p-0">
                {laterDates.slice(0, LATER_DATES_SHOWN).map((later) => (
                  <li key={later.id}>
                    <EventDateLink event={later} links={links}>
                      {eventDates(later, locale)?.when}
                    </EventDateLink>
                  </li>
                ))}
              </ul>
              {unlistedDateCount > 0 && (
                <p className="tw:text-xs tw:text-gray-500">{t('more_dates', { count: unlistedDateCount })}</p>
              )}
            </div>
          )}
          {intro && <p className="tw:line-clamp-3 tw:text-sm tw:text-gray-700">{intro}</p>}
          {coverage && (
            <div className="tw:pt-1">
              <CoverageBar filled={spots.filled} total={spots.total} />
            </div>
          )}
          <div className="tw:mt-auto tw:flex tw:flex-wrap tw:items-center tw:gap-2 tw:pt-2">
            <EventPageLink event={event} links={links} rsvpLabel={rsvpLabel} full={full} />
            <CalendarButton event={event} locale={locale} />
            {event.url && <ShareButton name={event.name} url={event.url} />}
          </div>
        </div>
      </div>
    </article>
  )
}

const NEW_TAB = { target: '_blank', rel: 'noopener' }

const CALENDARS = "['google','apple','outlookcom','ms365','ical']"

const CALENDAR_BUTTON_STYLE = [
  "--font: 'Inter var', ui-sans-serif, system-ui, sans-serif",
  '--wrapper-padding: 0',
  '--btn-background: #fff',
  '--btn-hover-background: #f9fafb',
  '--btn-border: #d1d5db',
  '--btn-hover-border: #d1d5db',
  '--btn-border-radius: 0.5rem',
  '--btn-text: #374151',
  '--btn-hover-text: #374151',
  '--btn-padding-x: 0.875rem',
  '--btn-padding-y: 0.5rem',
  '--btn-shadow: none',
  '--btn-hover-shadow: none',
  '--btn-active-shadow: none',
  '--list-background: #fff',
  '--list-border: #e5e7eb',
  '--list-border-radius: 0.375rem',
  '--list-hover-background: #f3f4f6',
  '--list-text: #374151',
  '--list-hover-text: #374151',
  '--list-font-weight: 400',
  '--accent-color: #374151',
].join('; ')

function CalendarButton({ event, locale }: { event: PublicEvent; locale: string }) {
  const details = useMemo(() => calendarButtonEvent(event), [event])
  const language = locale.startsWith('it') ? 'it' : 'en'
  const host = useRef<HTMLElement>(null)

  useEffect(() => {
    void loadCalendarButton(language).then(() => {
      if (host.current) styleCalendarList(host.current)
    })
  }, [language, details])

  useEffect(() => {
    const element = host.current
    if (!element) return

    let choosingByKey = false
    let keyHeld = false
    const refocus = () =>
      setTimeout(() => {
        if (document.activeElement === document.body) element.shadowRoot?.querySelector('button')?.focus()
      })
    const onKeyDown = (event: KeyboardEvent) => {
      choosingByKey = event.key === 'Enter' || event.key === ' '
      keyHeld = true
    }
    const onKeyUp = () => {
      keyHeld = false
    }
    const onFocusOut = (event: FocusEvent) => {
      if (!choosingByKey || event.relatedTarget) return
      choosingByKey = false
      if (keyHeld) window.addEventListener('keyup', refocus, { once: true })
      else refocus()
    }
    element.addEventListener('keydown', onKeyDown)
    element.addEventListener('focusout', onFocusOut)
    window.addEventListener('keyup', onKeyUp, true)
    return () => {
      element.removeEventListener('keydown', onKeyDown)
      element.removeEventListener('focusout', onFocusOut)
      window.removeEventListener('keyup', onKeyUp, true)
      window.removeEventListener('keyup', refocus)
    }
  }, [details])

  if (!details) return null

  return (
    <add-to-calendar-button
      ref={host}
      className="tw:[&::part(atcb-button)]:m-0 tw:[&::part(atcb-button)]:h-auto tw:[&::part(atcb-button)]:w-auto tw:[&::part(atcb-button)]:rounded-(--embed-radius-sm,0.5rem) tw:[&::part(atcb-button)]:px-3.5 tw:[&::part(atcb-button)]:py-2"
      {...details}
      options={CALENDARS}
      label={t('add_to_calendar')}
      hideTextLabelButton
      hideIconList
      hideRichData
      hideBackground
      size="3"
      buttonStyle="simple"
      listStyle="dropdown"
      lightMode="light"
      language={language}
      hideBranding
      styleLight={CALENDAR_BUTTON_STYLE}
    />
  )
}

function ShareButton({ name, url }: { name: string; url: string }) {
  const coarsePointer = useMediaQuery('(pointer: coarse)')
  const [copied, copy] = useCopied()
  const [triedSharing, setTriedSharing] = useState(false)
  const shareSheet = coarsePointer && 'share' in navigator && allowsFeature('web-share')
  const canCopy = allowsFeature('clipboard-write')
  const encodedUrl = encodeURIComponent(url)
  const label = t('share', { name })
  const icon = <Icon type="share-07" className="tw:h-4 tw:w-4" />
  const share = () => {
    setTriedSharing(true)
    navigator.share({ title: name, url }).catch((error: unknown) => {
      if (canCopy && !(error instanceof DOMException && error.name === 'AbortError')) copy(url)
    })
  }

  return (
    <>
      {shareSheet ? (
        <EmbedButton color="neutral" outline aria-label={label} onClick={share}>
          {icon}
        </EmbedButton>
      ) : (
        <DropdownMenu onOpenChange={(open) => open && setTriedSharing(true)}>
          <DropdownMenuTrigger asChild>
            <EmbedButton color="neutral" outline aria-label={label}>
              {icon}
            </EmbedButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {canCopy && <DropdownMenuItem onSelect={() => copy(url)}>{t('copy_link')}</DropdownMenuItem>}
            <DropdownMenuItem asChild>
              <a href={`mailto:?subject=${encodeURIComponent(name)}&body=${encodedUrl}`}>{t('email')}</a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={`https://wa.me/?text=${encodeURIComponent(`${name} ${url}`)}`} {...NEW_TAB}>
                WhatsApp
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} {...NEW_TAB}>
                Facebook
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {triedSharing && (
        <span role="status" className="tw:text-sm tw:text-gray-700">
          {copied && t('link_copied')}
        </span>
      )}
    </>
  )
}
